import { useState, useCallback } from "react";
import { Bluelagoon } from "@bluelagoon";
import axios from "axios";
import { makeId } from "..";
import { cleanFileName } from "@utils";

const FILE_SIZE_LIMIT = 20 * 1024 * 1024; // 20 MB

export const handleUpload = async (file, integrationId, isPublic = false) => {
    const cleanedFileName = cleanFileName(file.name);

    return new Promise((resolve, reject) => {
        Bluelagoon.signedUrl({
            fileName: cleanedFileName,
            fileType: file.type,
            integrationId,
            isPublic,
        })
            .then((res) => {
                const { url, key, permanentUrl, bucket, region } = res.data;
                axios
                    .put(url, file, {
                        headers: {
                            "Content-Type": file.fileType,
                        },
                    })
                    .then(() => {
                        resolve({ url, key, permanentUrl, bucket, region });
                    })
                    .catch((error) => {
                        console.error("Error uploading file:", error);
                        reject(error);
                    });
            })
            .catch((error) => {
                console.error("Error getting signed URL:", error);
                reject(error);
            });
    });
};

export function useUploader({
    onFileUpload,
    integrationId,
    isPublic = false,
} = {}) {
    const [loading, setLoading] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState([]);

    const uploadFiles = useCallback(
        async (files) => {
            setLoading(true);

            // Convert FileList to array and create initial status objects
            const newFiles = Array.from(files).map((file) => {
                const actualFile = file?.file ? file.file : file;
                if (actualFile.size > FILE_SIZE_LIMIT) {
                    return {
                        file: actualFile,
                        id: makeId(5),
                        name: actualFile.name,
                        status: "error",
                        progress: 0,
                        error: `File size cannot exceed 20 MB.`,
                        key: null,
                        params: file?.params ? file.params : {},
                    };
                }
                return {
                    file: actualFile,
                    id: makeId(5),
                    name: actualFile.name,
                    status: "uploading",
                    progress: 0,
                    error: null,
                    key: null,
                    params: file?.params ? file.params : {},
                };
            });

            // Add new files to the state
            setUploadedFiles((prev) => [...prev, ...newFiles]);

            // Process each file
            const uploadPromises = newFiles.map(async (fileObj) => {
                if (fileObj.status === "error") {
                    return {
                        success: false,
                        id: fileObj.id,
                        error: fileObj.error,
                    };
                }
                try {
                    // Make images public automatically
                    const shouldBePublic =
                        isPublic || fileObj.file.type.startsWith("image/");

                    const { url, key, permanentUrl, bucket, region } =
                        await handleUpload(
                            fileObj.file,
                            integrationId,
                            shouldBePublic
                        );
                    const updatedFile = {
                        ...fileObj,
                        status: "completed",
                        progress: 100,
                        key,
                        url,
                        permanentUrl,
                        bucket,
                        region,
                        integrationId,
                    };

                    // Call onFileUpload callback if provided
                    // IMPORTANT: Do this BEFORE updating status to "completed" to avoid race conditions
                    // where effects see "completed" status before file is added to stagedFiles
                    let uploadedFile = null;
                    if (onFileUpload) {
                        uploadedFile = await onFileUpload({
                            key,
                            url,
                            permanentUrl,
                            bucket,
                            region,
                            file: fileObj.file,
                            fileData: updatedFile,
                            params: fileObj.params,
                            integrationId,
                        });
                    }

                    // Update status to "completed" AFTER onFileUpload completes
                    setUploadedFiles((prev) =>
                        prev.map((f) => (f.id === fileObj.id ? updatedFile : f))
                    );

                    return { success: true, id: fileObj.id, file: uploadedFile };
                } catch (error) {
                    console.error(error);
                    setUploadedFiles((prev) =>
                        prev.map((f) =>
                            f.id === fileObj.id
                                ? {
                                      ...f,
                                      status: "error",
                                      error: error.message,
                                  }
                                : f
                        )
                    );
                    return { success: false, id: fileObj.id, error };
                }
            });

            try {
                const results = await Promise.all(uploadPromises);
                return results;
            } finally {
                setLoading(false);
            }
        },
        [onFileUpload]
    );

    const removeFile = useCallback((fileId) => {
        setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
    }, []);

    const clearFiles = useCallback(() => {
        setUploadedFiles([]);
    }, []);

    return {
        loading,
        files: uploadedFiles,
        uploadFiles,
        removeFile,
        clearFiles,
    };
}
